import { useState } from 'react';
import { Users, Calendar, Heart, MapPin, DollarSign, Star, ArrowRight, ArrowLeft } from 'lucide-react';
import Button from '../base/Button';

interface QuizStep {
  id: string;
  title: string;
  question: string;
  options: Array<{
    id: string;
    label: string;
    icon?: any;
    description?: string;
  }>;
}

export default function PersonalizationQuiz() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);

  const quizSteps: QuizStep[] = [
    {
      id: 'group',
      title: 'Votre groupe',
      question: 'Avec qui voyagez-vous ?',
      options: [
        { id: 'couple', label: 'En couple', icon: Heart, description: 'Expériences romantiques' },
        { id: 'family', label: 'En famille', icon: Users, description: 'Activités pour tous les âges' },
        { id: 'friends', label: 'Entre amis', icon: Users, description: 'Aventures et fêtes' },
        { id: 'solo', label: 'Solo', icon: Star, description: 'Découverte personnelle' }
      ]
    },
    {
      id: 'duration',
      title: 'Durée du séjour',
      question: 'Combien de temps restez-vous ?',
      options: [
        { id: '2-3', label: '2-3 jours', description: 'Weekend express' },
        { id: '4-6', label: '4-6 jours', description: 'Séjour classique' },
        { id: '7-10', label: '7-10 jours', description: 'Découverte complète' },
        { id: '10+', label: '10+ jours', description: 'Exploration approfondie' }
      ]
    },
    {
      id: 'interests',
      title: 'Vos intérêts',
      question: 'Qu\'est-ce qui vous attire le plus ?',
      options: [
        { id: 'adventure', label: 'Aventure', description: 'Safari, sports nautiques' },
        { id: 'luxury', label: 'Luxe', description: 'Spas, restaurants étoilés' },
        { id: 'culture', label: 'Culture', description: 'Musées, souks traditionnels' },
        { id: 'relaxation', label: 'Détente', description: 'Plages, piscines' }
      ]
    },
    {
      id: 'budget',
      title: 'Budget',
      question: 'Quel est votre budget par personne ?',
      options: [
        { id: 'budget', label: '500-1000€', description: 'Économique' },
        { id: 'mid', label: '1000-2000€', description: 'Confort' },
        { id: 'premium', label: '2000-3000€', description: 'Premium' },
        { id: 'luxury', label: '3000€+', description: 'Luxe' }
      ]
    }
  ];

  const handleAnswer = (stepId: string, answerId: string) => {
    setAnswers(prev => ({ ...prev, [stepId]: answerId }));
    
    if (currentStep < quizSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const getRecommendations = () => {
    const { group, duration, interests, budget } = answers;
    
    // Logic to generate personalized recommendations
    const recommendations = [];
    
    if (interests === 'adventure') {
      recommendations.push({
        title: 'Safari Premium dans le Désert',
        price: '65€',
        image: 'https://mesvacancesadubai.com/wp-content/uploads/2020/12/safari-dubai-desert-.jpg.webp',
        match: '95%'
      });
    }
    
    if (group === 'family') {
      recommendations.push({
        title: 'Aquaventure + Atlantis',
        price: '85€',
        image: 'https://mesvacancesadubai.com/wp-content/uploads/2020/12/aquaventure-.jpg.webp',
        match: '92%'
      });
    }
    
    if (interests === 'luxury') {
      recommendations.push({
        title: 'Petit-déjeuner avec Girafes',
        price: '508€',
        image: 'https://mesvacancesadubai.com/wp-content/uploads/2023/08/breakfast-with-the-girrafe-dinner-with-elephant-dubai-holidays-vacances-a-dubai.jpeg',
        match: '98%'
      });
    }
    
    return recommendations;
  };

  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 lg:bottom-[124px] left-6 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2 group"
        >
          <Star size={20} />
          <span className="font-medium">Trouvez vos activités idéales</span>
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {!showResults ? (
          <>
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Personnalisez votre séjour
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              {/* Progress bar */}
              <div className="flex items-center space-x-2 mb-4">
                {quizSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 flex-1 rounded-full ${
                      index <= currentStep ? 'bg-amber-600' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              
              <p className="text-gray-600">
                Étape {currentStep + 1} sur {quizSteps.length}
              </p>
            </div>

            {/* Current step */}
            <div className="p-6">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {quizSteps[currentStep].title}
                </h3>
                <p className="text-gray-600">
                  {quizSteps[currentStep].question}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quizSteps[currentStep].options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleAnswer(quizSteps[currentStep].id, option.id)}
                    className="p-4 border-2 border-gray-200 rounded-xl hover:border-amber-600 hover:bg-amber-50 transition-all duration-200 text-left group"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      {option.icon && (
                        <option.icon size={24} className="text-amber-600" />
                      )}
                      <span className="font-semibold text-gray-900 group-hover:text-amber-600">
                        {option.label}
                      </span>
                    </div>
                    {option.description && (
                      <p className="text-sm text-gray-600">
                        {option.description}
                      </p>
                    )}
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
                  disabled={currentStep === 0}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft size={16} />
                  <span>Précédent</span>
                </button>
                
                <span className="text-sm text-gray-500">
                  Cliquez sur une option pour continuer
                </span>
              </div>
            </div>
          </>
        ) : (
          /* Results */
          <div className="p-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star size={32} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Vos Recommandations Personnalisées
              </h2>
              <p className="text-gray-600">
                Basées sur vos préférences, voici nos suggestions
              </p>
            </div>

            <div className="space-y-4 mb-8">
              {getRecommendations().map((rec, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-xl hover:border-amber-600 transition-colors">
                  <img
                    src={rec.image}
                    alt={rec.title}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900">{rec.title}</h3>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        {rec.match} compatible
                      </span>
                    </div>
                    <p className="text-amber-600 font-semibold">À partir de {rec.price}</p>
                  </div>
                  <Button size="sm">
                    Réserver
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <Button 
                variant="outline" 
                onClick={resetQuiz}
                className="flex-1"
              >
                Refaire le quiz
              </Button>
              <Button 
                onClick={() => setIsOpen(false)}
                className="flex-1"
              >
                Voir toutes les activités
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}